import React, { useState, useEffect } from 'react';
import { studentService, countryService, stateService, districtService, universityService, collegeService } from '../../services/api';
import './AdminPage.css';

function AdminStudent() {
  const [students, setStudents] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [formData, setFormData] = useState({
    Name: '',
    Code: '',
    Password: '',
    MobileNo: '',
    Email: '',
    Address: '',
    Location: '',
    CountryId: '',
    StateId: '',
    DistrictId: '',
    UniversityId: '',
    CollegeId: '',
    UniversityRegNo: '',
    MarksheetPath: '',
    IsApproved: false
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
    loadDropdowns();
  }, []);

  useEffect(() => {
    if (formData.CountryId) {
      loadStates();
    }
  }, [formData.CountryId]);

  useEffect(() => {
    if (formData.StateId) {
      loadDistricts();
    }
  }, [formData.StateId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await studentService.getStudents();
      setStudents(data);
    } catch (error) {
      alert('Error loading students: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDropdowns = async () => {
    try {
      const [countriesData, universitiesData, collegesData] = await Promise.all([
        countryService.getCountries(),
        universityService.getUniversities(),
        collegeService.getColleges()
      ]);
      setCountries(countriesData);
      setUniversities(universitiesData);
      setColleges(collegesData);
    } catch (error) {
      console.error('Error loading dropdowns:', error);
    }
  };

  const loadStates = async () => {
    try {
      const data = await stateService.getStates();
      setStates(data.filter(s => s.CountryId === parseInt(formData.CountryId)));
    } catch (error) {
      console.error('Error loading states:', error);
    }
  };

  const loadDistricts = async () => {
    try {
      const data = await districtService.getDistricts();
      setDistricts(data.filter(d => d.StateId === parseInt(formData.StateId)));
    } catch (error) {
      console.error('Error loading districts:', error);
    }
  };

  const handleAdd = () => {
    setEditingStudent(null);
    setFormData({
      Name: '', Code: '', Password: '', MobileNo: '', Email: '', Address: '', Location: '',
      CountryId: '', StateId: '', DistrictId: '', UniversityId: '', CollegeId: '',
      UniversityRegNo: '', MarksheetPath: '', IsApproved: false
    });
    setIsModalOpen(true);
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setFormData({
      Id: student.Id,
      Name: student.Name || '',
      Code: student.Code || '',
      Password: '',
      MobileNo: student.MobileNo || '',
      Email: student.Email || '',
      Address: student.Address || '',
      Location: student.Location || '',
      CountryId: student.CountryId || '',
      StateId: student.StateId || '',
      DistrictId: student.DistrictId || '',
      UniversityId: student.UniversityId || '',
      CollegeId: student.CollegeId || '',
      UniversityRegNo: student.UniversityRegNo || '',
      MarksheetPath: student.MarksheetPath || '',
      IsApproved: student.IsApproved || false
    });
    setIsModalOpen(true);
  };

  const handleApprove = async (student) => {
    try {
      await studentService.approveStudent(student.Id);
      loadData();
    } catch (e) {
      alert('Error approving: ' + e.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await studentService.deleteStudent(id);
        loadData();
      } catch (error) {
        alert('Error deleting student: ' + error.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = { ...formData };
      if (payload.UniversityRegNo === '') payload.UniversityRegNo = null;
      if (payload.MarksheetPath === '') payload.MarksheetPath = null;
      await studentService.saveStudent(payload);
      setIsModalOpen(false);
      loadData();
    } catch (error) {
      alert('Error saving student: ' + error.message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="admin-page">
      <div className="page-header page-headeradmin">
        <h1>Student Management</h1>
        <button className="btn-primary" onClick={handleAdd}>Add Student</button>
      </div>

      {loading && !students.length ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Univ. Reg No</th>
                <th>Marksheet</th>
                <th>Approved</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student) => (
                <tr key={student.Id}>
                  <td>{student.Name}</td>
                  <td>{student.Code}</td>
                  <td>{student.Email}</td>
                  <td>{student.MobileNo}</td>
                  <td>{student.UniversityRegNo || '-'}</td>
                  <td>
                    {student.MarksheetPath ? (
                      <button type="button" className="btn-view-pdf" onClick={() => studentService.downloadMarksheet(student.Id, (student.MarksheetPath || '').split(/[/\\]/).pop() || 'marksheet.pdf')}>View PDF</button>
                    ) : '-'}
                  </td>
                  <td>{student.IsApproved ? 'Yes' : 'No'}</td>
                  <td>
                    {!student.IsApproved && (
                      <button className="btn-edit" style={{ marginRight: 6 }} onClick={() => handleApprove(student)}>Approve</button>
                    )}
                    <button className="btn-edit" onClick={() => handleEdit(student)}>Edit</button>
                    <button className="btn-delete" onClick={() => handleDelete(student.Id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingStudent ? 'Edit Student' : 'Add Student'}</h2>
              <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.Name}
                    onChange={(e) => setFormData({ ...formData, Name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Code *</label>
                  <input
                    type="text"
                    value={formData.Code}
                    onChange={(e) => setFormData({ ...formData, Code: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Password {editingStudent ? '' : '*'}</label>
                  <input
                    type="password"
                    value={formData.Password}
                    onChange={(e) => setFormData({ ...formData, Password: e.target.value })}
                    required={!editingStudent}
                    placeholder={editingStudent ? "Leave blank to keep current password" : ""}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.Email}
                    onChange={(e) => setFormData({ ...formData, Email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Mobile No</label>
                  <input
                    type="text"
                    value={formData.MobileNo}
                    onChange={(e) => setFormData({ ...formData, MobileNo: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.Address}
                    onChange={(e) => setFormData({ ...formData, Address: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={formData.Location}
                    onChange={(e) => setFormData({ ...formData, Location: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <select
                    value={formData.CountryId}
                    onChange={(e) => setFormData({ ...formData, CountryId: e.target.value, StateId: '', DistrictId: '' })}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.Id} value={country.Id}>{country.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>State</label>
                  <select
                    value={formData.StateId}
                    onChange={(e) => setFormData({ ...formData, StateId: e.target.value, DistrictId: '' })}
                    disabled={!formData.CountryId}
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option key={state.Id} value={state.Id}>{state.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>District</label>
                  <select
                    value={formData.DistrictId}
                    onChange={(e) => setFormData({ ...formData, DistrictId: e.target.value })}
                    disabled={!formData.StateId}
                  >
                    <option value="">Select District</option>
                    {districts.map((district) => (
                      <option key={district.Id} value={district.Id}>{district.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>University</label>
                  <select
                    value={formData.UniversityId}
                    onChange={(e) => setFormData({ ...formData, UniversityId: e.target.value })}
                  >
                    <option value="">Select University</option>
                    {universities.map((university) => (
                      <option key={university.Id} value={university.Id}>{university.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>College</label>
                  <select
                    value={formData.CollegeId}
                    onChange={(e) => setFormData({ ...formData, CollegeId: e.target.value })}
                  >
                    <option value="">Select College</option>
                    {colleges.map((college) => (
                      <option key={college.Id} value={college.Id}>{college.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>University Reg No</label>
                  <input
                    type="text"
                    value={formData.UniversityRegNo}
                    onChange={(e) => setFormData({ ...formData, UniversityRegNo: e.target.value })}
                    placeholder="University registration number"
                  />
                </div>
                {editingStudent && (
                  <>
                    <div className="form-group">
                      <label>Marksheet</label>
                      {formData.MarksheetPath ? (
                        <button type="button" className="btn-view-pdf" onClick={() => studentService.downloadMarksheet(editingStudent.Id, 'marksheet.pdf')}>View PDF</button>
                      ) : <span>-</span>}
                    </div>
                    <div className="form-group">
                      <label>Approved</label>
                      <span>{formData.IsApproved ? 'Yes' : 'No'}</span>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminStudent;

